import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    fetchOrthodontistDetail,
    updateOrthodontist,
    type OrthodontistDetail,
    type UpdateOrthodontistBody
} from '../services/orthodontistAccounts';
import {
    ACCOUNTS_ORTHODONTISTS_INFINITE_KEY,
    ACCOUNTS_ORTHODONTISTS_LIST_KEY,
    ORTHODONTIST_DETAIL_KEY
} from './queryKeys';

export function useOrthodontistDetail(orthodontistId: string | undefined) {
    const query = useQuery({
        queryKey: ORTHODONTIST_DETAIL_KEY(orthodontistId ?? ''),
        queryFn: () => fetchOrthodontistDetail(orthodontistId as string),
        enabled: Boolean(orthodontistId),
        staleTime: 60_000
    });

    return {
        orthodontist: query.data?.orthodontist,
        practices: query.data?.practices ?? [],
        isLoading: query.isLoading,
        isError: query.isError,
        refetch: query.refetch
    };
}

export function useUpdateOrthodontistDetail(orthodontistId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (body: UpdateOrthodontistBody) => updateOrthodontist(orthodontistId, body),
        onSuccess: (updated) => {
            queryClient.setQueryData<OrthodontistDetail>(
                ORTHODONTIST_DETAIL_KEY(orthodontistId),
                (prev) => prev && { ...prev, orthodontist: { ...prev.orthodontist, ...updated } }
            );
            queryClient.invalidateQueries({ queryKey: ACCOUNTS_ORTHODONTISTS_LIST_KEY });
            queryClient.invalidateQueries({ queryKey: ACCOUNTS_ORTHODONTISTS_INFINITE_KEY });
        }
    });
}
